import { reactive, ref, Ref, UnwrapRef } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH, queryAsyncTask } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
const columns = [
  {
    colKey: 'creationTime',
    title: i18n_t('同步时间'),
    width: 180,
    fixed: 'left',
  },
  {
    colKey: 'status',
    width: 200,
    title: i18n_t('状态'),
  },
  {
    colKey: 'operate',
    width: 80,
    // fixed: 'right',
  },
];

interface Handler {
  columns: Array<any>;
  show: Ref<boolean>;
  loading: Ref<boolean>;
  latestTask: Ref<null | SMH.Task<SMH.SyncWeworkOrgResultData | SMH.AsyncTaskResult>>;
  tableData: UnwrapRef<{ list: SMH.Task<SMH.SyncWeworkOrgResultData | SMH.AsyncTaskResult>[]; total: number }>;
  methods: {
    reset: () => void;
    pullList: (action: CoFile.SyncType) => Promise<void>;
    isSyncBefore: (action: CoFile.SyncType) => Promise<boolean>;
    triggerSyncNow: ({
      action,
      syncWay,
      taskReadyFn,
    }: {
      action: CoFile.SyncType;
      syncWay?: string;
      taskReadyFn?: Function;
    }) => Promise<void>;
  };
}
export default function handler(): Handler {
  const show = ref(false);
  const loading = ref(false);
  // 最近的一个同步任务
  const latestTask: Handler['latestTask'] = ref(null);
  const tableData: Handler['tableData'] = reactive({
    list: [],
    total: 0,
  });

  // 只需要创建一个查询任务，不要重复查询
  let isChecking = false;
  const methods = {
    reset() {
      Object.assign(tableData, { list: [], total: 0 });
      [latestTask.value] = tableData.list;
    },
    async pullList(action: CoFile.SyncType) {
      loading.value = true;
      try {
        const { data } = await smh.userAPI.getSyncWeworkOrgList({ action });
        tableData.list = data.contents;
        tableData.total = data.totalNum;
        if (tableData.list.length > 0) {
          [latestTask.value] = tableData.list;
          this.checkSyncingTask();
        }
      } catch (error) {
        tips.error(error as MsgError);
      }
      loading.value = false;
    },
    /**
     * @description 是否之前同步成功过数据，结合选择登录时提示使用
     */
    async isSyncBefore(action: CoFile.SyncType) {
      let isSync = false;
      try {
        const { data } = await smh.userAPI.getSyncWeworkOrgList({ action });
        isSync = data.totalNum !== 0;
      } catch (err) {
        tips.error(err as MsgError);
      } finally {
        return isSync;
      }
    },
    /**
     * @description 检查正在同步中的任务
     */
    async checkSyncingTask(_taskId?: string | number) {
      if (isChecking) return;
      isChecking = true;
      if (_taskId || latestTask.value?.status === 202) {
        try {
          const taskId = _taskId || latestTask.value?.taskId;
          const res = await queryAsyncTask(taskId as number);
          latestTask.value = res;
        } catch (error) {
          tips.error(error as MsgError);
        }
      }
      isChecking = false;
    },
    /**
     * @description 立即同步
     */
    async triggerSyncNow({
      action,
      syncWay,
      taskReadyFn,
    }: {
      action: CoFile.SyncType;
      syncWay?: string;
      taskReadyFn?: Function;
    }) {
      try {
        latestTask.value = { status: 202 };
        let params;
        if (action === 'SyncAccountOrg' && syncWay) {
          params = { action, data: { result: { syncWay } } };
        } else {
          params = { action };
        }
        const { data } = await smh.userAPI.triggerSyncOrg(params);
        if (data.taskId) {
          const { taskId } = data;
          latestTask.value = {
            taskId,
            status: 202,
          };
          taskReadyFn && taskReadyFn();
          await this.checkSyncingTask(taskId);
        } else {
          latestTask.value = data;
        }
      } catch (error: any) {
        let errMsg = i18n_t('同步失败，{slot1}', {
          slot1: formatError(error as MsgError, true),
        });
        if (error?.status === 500) {
          errMsg = i18n_t('同步失败，服务器错误');
        }
        latestTask.value = {
          status: error?.status || 500,
        };
        tips.error(errMsg);
      }
    },
  };
  return {
    columns,
    show,
    loading,
    latestTask,
    tableData,
    methods,
  };
}
